<template>
  <el-form label-position="top" :model="form" require-asterisk-position="right">
    <!-- Name -->
    <el-form-item
      :error="form.getErrorMessage('name')"
      :label="i18n.t('general.name')"
      prop="form.fields.name"
      label-position="top">
      <SmInput v-model="form.fields.name" design="form" />
    </el-form-item>

    <!-- Notes -->
    <el-form-item
      :error="form.getErrorMessage('notes')"
      :label="i18n.t('notes')"
      prop="form.fields.notes"
      label-position="top">
      <SmInput v-model="notes" design="form" type="area" />
    </el-form-item>

    <el-form-item
      v-if="!('external_id' in form.fields) || !form.fields.external_id">
      <div class="mb-3 flex items-center gap-3">
        <SmSwitch v-model="form.fields.auto_assign!.add_to_new_user" />
        <p>
          {{ i18n.t('assignToNewUsers') }}
        </p>
      </div>

      <div class="flex items-center gap-3">
        <SmSwitch v-model="form.fields.auto_assign!.add_to_new_guest" />
        <p>
          {{ i18n.t('assignToNewGuests') }}
        </p>
      </div>
    </el-form-item>
  </el-form>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { SoftwareLicense } from '@/client'
  import useForm from '@/composables/useForm'
  import { isRequired } from '../sm/SmInput/SmInputValidator'
  import {
    saveNewSoftwareLicense,
    updateSoftwareLicense,
  } from '@/utils/license'
  import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'

  const i18n = useI18n()
  const applicationDetailsStore = useApplicationDetailsStore()

  export type LicenseModelFormModel = {
    license: SoftwareLicense
  }

  const model = defineModel<LicenseModelFormModel>('softwareLicense', {
    required: true,
  })

  // Set defaults
  if (!model.value.license.auto_assign) model.value.license.auto_assign = {}

  if ('auto_assign' in model.value && model.value.auto_assign == undefined)
    model.value.auto_assign = {
      add_to_new_guest: false,
    }

  const notes = computed({
    get: () => form.fields.notes ?? undefined,
    set: (val) => {
      form.fields.notes = val || null
    },
  })

  const form = useForm(
    {
      ...toRaw(model.value.license),
    },
    // Form Hooks
    {},
    // Form Validators
    {
      name: [isRequired],
    },
    {
      name: i18n.t('name'),
    }
  )

  async function submitForms(method: 'update' | 'create') {
    let licenseId: string = model.value.license._id

    // submit license model form
    await form.submit(async () => {
      // Update in backend
      if (method === 'update') {
        licenseId = model.value.license._id
        if (form.dirty) {
          await updateSoftwareLicense(form.fields, licenseId)
        }
      } else if (method === 'create') {
        // Create in backend
        if (applicationDetailsStore.application?._id) {
          await saveNewSoftwareLicense(
            form.fields,
            applicationDetailsStore.application._id
          ).then((res) => {
            licenseId = (res as SoftwareLicense)._id
          })
        }
      }
    })
  }

  function validate() {
    const results: boolean[] = []
    results.push(form.validate())
    const result = results.every((result) => result === true)
    return result
  }

  defineExpose({
    validate,
    submitForms,
    form,
  })
</script>

<style scoped lang="scss"></style>
